import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import tw from 'twin.macro';
import { closestCenter, DndContext, PointerSensor, useSensor } from '@dnd-kit/core';
import {
  useSortable,
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { PlaylistContext } from '../../context';
import { Icon } from '../../icon';
import { Small } from '../../text';
import {
  TrashIcon,
  VideoIcon,
  ImageIcon,
  ExternalUrlIcon,
  ArrowFullIcon,
  DragHandleIcon,
} from 'icons';
import { formatNumber, formatDate, formatDuration } from '../../../lib/helpers';
import { KudoAmount } from '../../kudos';
import { ItemImage } from '../../item';
import { Button } from '../../button';
import { alterPlaylistOrder } from 'services/post';
import { PLAYLIST_PATH } from '../../../data/';

export const PlaylistItemDragAndDrop = ({ playlistId }) => {
  const { playlistItems } = useContext(PlaylistContext);

  const [items, setItems] = useState(playlistItems);

  useEffect(() => {
    setItems(playlistItems);
  }, [playlistItems]);

  const sensors = [useSensor(PointerSensor)];

  const handleDragEnd = ({ active, over }) => {
    if (active.id !== over.id) {
      const newOrderList = (items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      };

      const newOrderPositions = [
        ...newOrderList(items).map((item, index) => ({ id: item.id, position: Number(index) })),
      ];
      alterPlaylistOrder({ newPositionList: newOrderPositions, playlistId });
      setItems(newOrderList);
    }
  };

  return (
    <>
      <div tw='flex items-center gap-2 py-3 border-b border-icons border-opacity-60 justify-between font-bold min-w-[600px]'>
        <div tw='w-8 flex-shrink-0'>&nbsp;</div>
        <div tw='w-[280px] md:w-auto md:flex-grow'>Titel</div>
        <div tw='w-20'>Kudos</div>
        <div tw='w-20'>Media</div>
        <div tw='w-24'>Acties</div>
      </div>

      <div tw='w-full'>
        {items && items.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={items.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {items.map((item) => (
                <SortableItem item={item} key={item.id} playlistId={playlistId} handle />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </>
  );
};

const SortableItem = ({ item, playlistId }) => {
  const { currentUserId, currentPlaylistId, deleteItemFromPlaylist } = useContext(PlaylistContext);

  const {
    id,
    title,
    date,
    position,
    composite_id: compositeId,
    media_type: mediaType,
    stats: { kudos_total: kudosTotal, views_total: viewsTotal },
    media,
  } = item;

  const { setNodeRef, attributes, listeners, transition, transform, isDragging } = useSortable({
    id,
  });

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  const [beingDeleted, setBeingDeleted] = useState(false);

  return (
    <div tw='relative'>
      <div
        ref={setNodeRef}
        style={style}
        css={[
          tw`flex items-center gap-2 py-3 border-b border-icons border-opacity-60 justify-between min-w-[600px]`,
          beingDeleted && tw`!opacity-30`,
        ]}
      >
        <div tw='flex-shrink-0'>
          <Link to={`${PLAYLIST_PATH.public}/${currentUserId}/${playlistId}/${compositeId}`}>
            <Button
              iconProps={{ Type: ArrowFullIcon, size: 'small', color: 'dark' }}
              tw='-rotate-90 w-7 h-7'
              primary
            />
          </Link>
        </div>
        <div tw='w-[280px] md:w-auto md:flex-grow'>
          <div tw='flex items-center'>
            <div tw='h-14 w-14 flex-shrink-0'>
              <ItemImage item={item} isPlaylistItem hideSummary />
            </div>
            <div tw='ml-4'>
              <div tw='font-bold line-clamp-1'>{title}</div>
              <Small>
                {formatDate(date)} | Views: {formatNumber(viewsTotal)}
              </Small>
            </div>
          </div>
        </div>
        <div tw='w-20'>
          <KudoAmount numKudos={kudosTotal} hideActions leftAlign />
        </div>
        <div tw='flex gap-2 items-center w-20'>
          <Icon Type={mediaType === 'VIDEO' ? VideoIcon : ImageIcon} size='small' color='icons' />
          {formatDuration(media[0].duration, 1, 'VIDEO')}
        </div>
        <div tw='w-24 flex gap-4'>
          {!beingDeleted && (
            <>
              <div
                onClick={() => {
                  if (window.confirm('Weet je zeker dat je dit item wilt verwijderen?')) {
                    setBeingDeleted(true);
                    deleteItemFromPlaylist({
                      playlistId: currentPlaylistId,
                      itemPostition: position,
                    });
                  }
                }}
              >
                <Icon
                  Type={TrashIcon}
                  size='medium'
                  tw='transition-all cursor-pointer hover:scale-125 hover:text-danger mx-auto'
                />
              </div>
              <a href={`/item/${compositeId}`} target='_blank' rel='noopener noreferrer'>
                <Icon
                  Type={ExternalUrlIcon}
                  size='medium'
                  tw='transition-all cursor-pointer hover:scale-125 hover:text-opacity-60 mx-auto'
                />
              </a>
              <div {...listeners} {...attributes}>
                <Icon
                  Type={DragHandleIcon}
                  size='medium'
                  tw='transition-all cursor-pointer hover:scale-125 mx-auto'
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
